import { FC } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { CoreIconButton } from '../../../_core/components/_ui/core-button';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import mongoose from 'mongoose';

export type QuestionOptionsProps = {
	questionIndex: number;
};

export const QuestionOptionsArray: FC<QuestionOptionsProps> = (props) => {
	const { questionIndex } = props;
	const [t] = useTranslation();
	const {
		watch,
		control,
		formState: { errors },
	} = useFormContext();
	const { fields, append, remove } = useFieldArray({
		control,
		name: `questions.${questionIndex}.options`,
	});

	return (
		<Box sx={{ marginTop: '16px' }}>
			{fields.map((field, index) => (
				<Box key={field.id} sx={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
					<Controller
						name={`questions.${questionIndex}.options.${index}.text`}
						control={control}
						render={({ field }) => (
							<CoreTextField
								{...field}
								fullWidth
								disabled={watch(`questions.${questionIndex}.options.${index}`)?.isOtherOption}
								placeholder={t('survey-templates.forms.placeholders.enter-description')}
								error={!!(errors['questions'] && (errors['questions'] as any)[questionIndex]?.options?.[index]?.text)}
								helperText={
									errors['questions'] && (errors['questions'] as any)[questionIndex]?.options?.[index]?.text?.message
								}
							/>
						)}
					/>
					<CoreIconButton
						onClick={() => {
							remove(index);
						}}
						sx={{ marginLeft: '16px' }}
					>
						<CloseIcon />
					</CoreIconButton>
				</Box>
			))}
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<Button
					onClick={() => {
						append({ _id: new mongoose.Types.ObjectId().toString(), text: '', isOtherOption: false });
					}}
					variant="text"
					sx={{ py: '4px', px: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}
				>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M2 8H14" stroke="#3C8DBC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M8 2V14" stroke="#3C8DBC" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
					</svg>

					<Typography
						sx={{
							color: (theme) => theme.colors.lightBlue,
							textAlign: 'center',
							fontSize: '16px',
							fontWeight: 500,
							textTransform: 'none',
						}}
					>
						{t('survey-templates.forms.buttons.add-option')}
					</Typography>
				</Button>
				{!fields?.find((option: any) => option.isOtherOption) ? (
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Typography
							sx={{
								color: '#9397A4',
								textAlign: 'center',
								fontSize: '16px',
								fontWeight: 500,
								lineHeight: '24px',
								px: 1,
							}}
						>
							or
						</Typography>
						<Button
							onClick={() => {
								append({ _id: new mongoose.Types.ObjectId().toString(), text: 'Other', isOtherOption: true });
							}}
							variant="text"
							sx={{ py: '4px', px: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}
						>
							<Typography
								sx={{
									color: (theme) => theme.colors.lightBlue,
									textAlign: 'center',
									fontSize: '16px',
									fontWeight: 500,
									textTransform: 'none',
								}}
							>
								{t('survey-templates.forms.buttons.add-other')}
							</Typography>
						</Button>
					</Box>
				) : null}
			</Box>
		</Box>
	);
};
